// Refactored by j-funk into functional component 2023/10
import React, { useState, useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { useCookies } from 'react-cookie';
import { useDeepCompareEffect } from 'react-use';
import { Col, Grid, Row, Tab, Tabs } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTermDatabases,
  fetchTermPredictions,
} from '../../actions/PredictionActions';
import { setGenes } from '../../actions/actions';
import {
  addNetwork,
  addNetworkGroup,
  fetchDatatypes,
  fetchIntegrations,
  fetchNetworkWithTerms,
  fetchRelevantNetworksAction,
  selectDatatypes,
  selectIntegration,
} from '../../actions/NetworkActions';
import { startTutorial } from '../../actions/TutorialActions';
import { BASE_URI, BP_DB, TERM_DATABASES, TISSUE_DB } from '../../settings';
import MultiNetwork from '../MultiNetwork';
import TermPredictions from '../TermPredictions';
import TissueExpressionHeatmap from '../TissueExpressionHeatmap';
import GeneHeader from '../GeneHeader';
import Tooltip from '../Tooltip';
import Tutorial from '../Tutorial';
import s from './GenePage.css';

const tissue = { group: 'tissue' };
const bp = { group: 'bp' };

// Show predictions tables when a single gene in queried
const termTabs = (db, idx, genes, termPredictions) => {
  // Map gene -> prediction list
  const predictions = Object.keys(termPredictions).reduce((obj, gene) => {
    if (termPredictions[gene][db.slug])
      Object.assign(obj, {
        [gene]: termPredictions[gene][db.slug],
      });
    return obj;
  }, {});

  return (
    <Tab title={db.title} eventKey={idx + 4} key={idx}>
      <div key={`${db.title}-${idx}`}>
        <h3 className={s.section}>
          {db.title} associations
          <br />
          <small>
            Experimentally associated and computationally predicted associations
          </small>
        </h3>
        <hr />
        <TermPredictions
          genes={genes}
          allPredictions={predictions}
          database={db.title}
          width={1100}
          height={700}
        />
      </div>
    </Tab>
  );
};

/*
 * Component for a single gene query. Displays a toolbar for adding networks
 * and term predictions for the queried gene
 */
const GenePage = props => {
  const dispatch = useDispatch();
  const targetRef = useRef(null);

  const { gstrs, defaultIntegration } = props;
  const {
    termPredictions = {},
    tissueNetworks = [],
    bpNetworks = [],
    integrations = [],
    datatypes = [],
    relevantNetworks = [],
  } = useSelector(state => {
    return {
      termPredictions: state.termPredictions,
      tissueNetworks: state.networkGroups.tissue,
      bpNetworks: state.networkGroups.bp,
      // Array of all available integrations
      integrations: state.integrations,
      // Array of datatypes the user can apply in integrations
      datatypes: state.datatypes,
      relevantNetworks: state.relevantNetworks,
    };
  });

  const [genes, setGenesState] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetchingNetwork, setIsFetchingNetwork] = useState(false);
  const [tissueIntegrations, setTissueIntegrations] = useState([]);
  const [bpIntegrations, setBpIntegrations] = useState([]);
  const [tissuePredictions, setTissuePredictions] = useState({});
  const [tissueTerms, setTissueTerms] = useState([]);

  const [cookies] = useCookies();

  useEffect(() => {
    const fetchGenes = async () => {
      const promises = gstrs.map(gstr =>
        fetch(`${BASE_URI}genes/${gstr}`).then(response =>
          response.ok
            ? response.json()
            : Promise.reject(new Error(`Failed to load ${gstrs}`)),
        ),
      );

      const results = await Promise.allSettled(promises);
      const validResults = results
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value);
      setGenesState(validResults);
    };

    if (gstrs.length > 0) {
      fetchGenes();
    }
  }, [gstrs]);

  // Replace componentDidMount
  useEffect(() => {
    const { tutorialStep } = cookies;
    if (!tutorialStep) dispatch(startTutorial());
  }, [cookies, dispatch]);

  // Replace componentDidMount
  useEffect(() => {
    // Add network groups
    dispatch(addNetworkGroup('tissue'));
    dispatch(addNetworkGroup('bp'));

    // Initialize selections
    dispatch(setGenes(genes, tissue));
    dispatch(setGenes(genes, bp));
    dispatch(setGenes(genes));

    // Fetch list of integrations
    dispatch(fetchIntegrations());

    // Fetch list of datatypes
    dispatch(fetchDatatypes());

    // Fetch relevant networks to the query
    dispatch(fetchRelevantNetworksAction(genes));

    // Fetch list of term databases
    dispatch(fetchTermDatabases());

    // Fetch predictions for queried genes
    for (let i = 0, n = genes.length; i < n; i += 1) {
      dispatch(fetchTermPredictions(genes[i], TISSUE_DB));

      for (let k = 0, N = TERM_DATABASES.length; k < N; k += 1) {
        const db = TERM_DATABASES[k];
        dispatch(fetchTermPredictions(genes[i], db.slug, db.params));
      }
    }
  }, [dispatch, genes]);

  useEffect(() => {
    // filter integrations for tissue and bp
    setTissueIntegrations(
      integrations.filter(
        integ =>
          (integ.context.term &&
            integ.context.term.database.slug === TISSUE_DB) ||
          integ.slug === 'global',
      ),
    );
    setBpIntegrations(
      integrations.filter(
        integ =>
          (integ.context.term && integ.context.term.database.slug === BP_DB) ||
          integ.slug === 'global',
      ),
    );
  }, [integrations]);

  useDeepCompareEffect(() => {
    const selectedIntegration = defaultIntegration
      ? integrations.find(
          integ =>
            integ.slug === defaultIntegration &&
            integ.context.term.database.slug === TISSUE_DB,
        )
      : null;

    const tissueNetworks = relevantNetworks.filter(
      net => net.context.term && net.context.term.database.slug === TISSUE_DB,
    );
    let integration = selectedIntegration;
    if (!integration) {
      if (tissueNetworks.length > 0) integration = tissueNetworks[0];
      else if (tissueIntegrations.length > 0)
        integration = tissueIntegrations[0];
    }
    if (integration && !isFetchingNetwork) {
      dispatch(selectIntegration(integration, tissue));
      dispatch(addNetwork(integration, genes, tissue));
      setIsFetchingNetwork(true);
      dispatch(
        fetchNetworkWithTerms(integration, genes, {
          group: 'tissue',
          idx: 0,
        }),
      ).then(() => {
        setLoading(false);
        setIsFetchingNetwork(false);
      });
    }

    const goNetworks = relevantNetworks.filter(
      net => net.context.term && net.context.term.database.slug === BP_DB,
    );
    integration = null;
    // Display the top network
    if (goNetworks.length > 0) integration = goNetworks[0];
    else if (bpIntegrations.length > 0) integration = bpIntegrations[0];
    if (integration) {
      dispatch(selectIntegration(integration, bp));
    }
  }, [dispatch, integrations, relevantNetworks, tissue]);

  useEffect(() => {
    dispatch(selectDatatypes(datatypes, tissue));
    dispatch(selectDatatypes(datatypes, bp));
  }, [dispatch, datatypes]);

  useEffect(() => {
    const terms = {};
    // Transform tissue predictions into expected data structure for viz
    // Reduces an array of predictions to map of id to pred
    const reducePred = (obj, pred) => {
      Object.assign(obj, { [pred.term.identifier]: pred });
      terms[pred.term.identifier] = pred.term;
      return obj;
    };

    // Reduces an array of genes to maps of genes to pred
    const reduceGene = (obj, gene) => {
      if (termPredictions[gene] && termPredictions[gene][TISSUE_DB])
        Object.assign(obj, {
          [gene]: termPredictions[gene][TISSUE_DB].reduce(reducePred, {}),
        });
      return obj;
    };

    setTissuePredictions(genes.map(x => x.entrez).reduce(reduceGene, {}));
    setTissueTerms(Object.keys(terms).map(id => terms[id]));
  }, [genes, termPredictions]);

  const tissueNetworksElement = (
    <div>
      <h3 className={s.section}>
        Tissue-specific interactions
        <br />
        <small>
          Functional networks that capture tissue-specific interactions from
          large data compendia
        </small>
      </h3>
      <hr />
      <MultiNetwork
        group="tissue"
        groupTitle="Tissue"
        genes={genes}
        isLoading={loading}
        networks={tissueNetworks}
        integrations={tissueIntegrations}
        datatypes={datatypes}
      />
    </div>
  );

  const bioNetworks = (
    <div>
      <h3 className={s.section}>
        Biological process-specific interactions
        <br />
        <small>
          Functional networks that capture interactions specific to biological
          processes and pathways
        </small>
      </h3>
      <hr />
      <MultiNetwork
        group="bp"
        groupTitle="Biological process"
        isLoading={loading}
        networks={bpNetworks}
        integrations={bpIntegrations}
        datatypes={datatypes}
      />
    </div>
  );

  const tissueExpression = (
    <div>
      <h3 className={s.section}>
        Tissue expression <br />
        <small>
          Experimentally validated and computationally predicted
          tissue/cell-type expression
        </small>
      </h3>
      <hr />
      <TissueExpressionHeatmap
        genes={genes}
        predictions={tissuePredictions}
        terms={tissueTerms}
      />
    </div>
  );

  const bioPredictions = TERM_DATABASES.map((db, idx) =>
    termTabs(db, idx, genes, termPredictions),
  );

  return (
    <div className={s.root}>
      <Grid>
        <Row>
          <Col md={12}>
            <GeneHeader genes={genes} />
            {genes.length > 1 ? <hr /> : null}
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Tabs
              id="analyses"
              className="tab-under"
              defaultActiveKey={1}
              bsStyle="pills"
            >
              <Tab
                eventKey={1}
                title={
                  <Tooltip text="Tissue-specific functional interactions">
                    Tissue gene network
                  </Tooltip>
                }
              >
                {tissueNetworksElement}
              </Tab>
              <Tab
                eventKey={2}
                title={
                  <Tooltip text="Tissue expression">Tissue expression</Tooltip>
                }
              >
                {tissueExpression}
              </Tab>
              <Tab
                eventKey={3}
                title={
                  <Tooltip text="Biological process-specific functional interactions">
                    Biological process gene network
                  </Tooltip>
                }
              >
                {bioNetworks}
              </Tab>
              {bioPredictions}
            </Tabs>
            <div
              ref={targetRef}
              style={{ position: 'relative', top: '-80px' }}
            />
            <Tutorial
              step={1}
              container={this}
              placement="bottom"
              target={targetRef.current}
              title="Get started!"
              text={
                <div>
                  Select an analysis of your query genes from this row. By
                  default the tissue network analysis of your query is selected
                  and displayed.
                  <br />
                  <br />
                  Start this tutorial at any time from the Help menu above.
                </div>
              }
            />
          </Col>
        </Row>
      </Grid>
    </div>
  );
};

GenePage.propTypes = {
  // Queried genes
  gstrs: PropTypes.arrayOf(PropTypes.string),
  defaultIntegration: PropTypes.string,
};

// Custom comparison function to determine if re-render is needed
function areArraysEqual(arr1, arr2) {
  if (arr1.length !== arr2.length) return false;
  for (let i = 0; i < arr1.length; i += 1) {
    if (arr1[i] !== arr2[i]) return false;
  }
  return true;
}

function arePropsEqual(prevProps, nextProps) {
  return (
    areArraysEqual(prevProps.gstrs, nextProps.gstrs) &&
    prevProps.defaultIntegration === nextProps.defaultIntegration
  );
}

export default withStyles(s)(memo(GenePage, arePropsEqual));
